
import {
  useEffect,
  useState,
} from "react";

import axios from "../../../utils/api";

import {
  getBookingRooms,
} from "../../../api/bookingEngine";

import {
  getRoomCategoryName,
  formatCategoryName,
} from "../utils/bookingRoomUtils";


const useBookingRooms = ({
  subdomain,
}) => {

  const [allRooms, setAllRooms] =
    useState([]);

  const [roomsData, setRooms] =
    useState([]);

  const [roomCategories, setRoomCategories] =
    useState([]);

  const [loadingRooms, setLoadingRooms] =
    useState(true);

  const [searchingAvailability, setSearchingAvailability] =
    useState(false);


  /* =========================================================
     LOAD ROOMS
  ========================================================= */

  useEffect(() => {

    if (!subdomain) {
      return;
    }


    let mounted = true;


    const loadRooms = async () => {

      try {

        if (mounted) {
          setLoadingRooms(true);
        }


        console.log(
          "🏨 Loading rooms for:",
          subdomain
        );


        /* =====================================================
           ROOM TYPES
        ===================================================== */

        const response =
          await getBookingRooms(
            subdomain
          );


        console.log(
          "🏨 ROOMS RESPONSE:",
          response
        );


        /* =====================================================
           INVENTORY
        ===================================================== */

        let inventoryMap = {};


        try {

          const inventoryResponse =
            await axios.get(
              `/booking-engine/${subdomain}/add-rooms`
            );


          console.log(
            "📦 INVENTORY RESPONSE:",
            inventoryResponse
          );


          const inventoryRooms =
            inventoryResponse?.data?.rooms ||
            [];


          inventoryRooms.forEach(
            (room) => {

              const typeId =
                room?.roomType?._id ||
                room?.roomTypeId;


              if (!typeId) {
                return;
              }


              if (!inventoryMap[typeId]) {

                inventoryMap[typeId] = {
                  total: 0,
                  available: 0,
                };

              }


              inventoryMap[typeId].total++;


              if (
                room.operationalStatus ===
                  "ACTIVE" &&
                room.occupancyStatus ===
                  "VACANT"
              ) {

                inventoryMap[typeId]
                  .available++;

              }

            }
          );


        } catch (inventoryError) {

          console.error(
            "❌ INVENTORY ERROR:",
            inventoryError
          );

        }


        /* =====================================================
           VALIDATE ROOM RESPONSE
        ===================================================== */

        if (!response?.success) {

          throw new Error(
            response?.message ||
            "Unable to load rooms."
          );

        }


        const apiRooms =
          response?.rooms || [];


        /* =====================================================
           FORMAT ROOMS
        ===================================================== */

        const formattedRooms =
          apiRooms.map(
            (room) => {

              const roomTypeId =
                room.roomTypeId ||
                room.id ||
                room._id;


              const inventory =
                inventoryMap[roomTypeId] || {
                  total: 0,
                  available: 0,
                };


              /* =============================================
                 CATEGORY
              ============================================= */

              const rawCategory =
                getRoomCategoryName(
                  room
                );


              const category =
                formatCategoryName(
                  rawCategory
                );


              return {

                /* ID */

                id:
                  room.id ||
                  room._id,

                roomTypeId,


                /* BASIC */

                name:
                  room.name ||
                  "Room",

                code:
                  room.code ||
                  "",

                description:
                  room.description ||
                  "",


                /* CATEGORY */

                category,


                /* TYPE */

                type:
                  room.name ||
                  "Room",


                /* IMAGE */

                image:
                  room.images?.[0] ||
                  "",

                images:
                  Array.isArray(
                    room.images
                  )
                    ? room.images
                    : [],


                /* CAPACITY */

                guests:
                  room.maxAdults ||
                  0,

                maxAdults:
                  room.maxAdults ||
                  0,

                maxChildren:
                  room.maxChildren ||
                  0,

                maxOccupancy:
                  room.maxOccupancy ||
                  0,


                /* BED */

                beds:
                  room.bedType
                    ? 1
                    : 0,

                bedType:
                  room.bedType ||
                  "",


                /* BATHROOM */

                bathrooms:
                  room.bathrooms ||
                  0,


                /* SIZE */

                size:
                  room.roomSize
                    ? `${room.roomSize} ${
                        room.roomSizeUnit ||
                        ""
                      }`
                    : "",

                roomSize:
                  room.roomSize ||
                  0,

                roomSizeUnit:
                  room.roomSizeUnit ||
                  "",


                /* PRICE */

                price:
                  Number(
                    room.price ??
                    room.todayPrice ??
                    room.basePrice ??
                    0
                  ),

                todayPrice:
                  Number(
                    room.todayPrice ??
                    room.price ??
                    room.basePrice ??
                    0
                  ),

                basePrice:
                  Number(
                    room.basePrice ||
                    0
                  ),


                /* EXTRA */

                extraAdultPrice:
                  Number(
                    room.extraAdultPrice ||
                    0
                  ),

                extraChildPrice:
                  Number(
                    room.extraChildPrice ||
                    0
                  ),

                extraBedAllowed:
                  Boolean(
                    room.extraBedAllowed
                  ),

                extraBedPrice:
                  Number(
                    room.extraBedPrice ||
                    0
                  ),


                /* AMENITIES */

                amenities:
                  Array.isArray(
                    room.amenities
                  )
                    ? room.amenities
                    : [],


                /* VIEW */

                viewType:
                  room.viewType ||
                  "",


                /* AVAILABILITY */

                available:
                  room.available ??
                  null,


                /* RATING */

                rating:
                  room.rating ||
                  0,

                reviews:
                  room.reviews ||
                  0,


                /* TIMINGS */

                checkInTime:
                  room.checkInTime ||
                  "",

                checkOutTime:
                  room.checkOutTime ||
                  "",


                /* INVENTORY */

                inventory:
                  inventory.total,

                inventoryAvailable:
                  inventory.available,


                /* ORIGINAL API DATA */

                apiData:
                  room,

              };

            }
          );


        console.log(
          "✅ FORMATTED ROOMS:",
          formattedRooms
        );


        /* =====================================================
           BUILD ROOM CATEGORIES
        ===================================================== */

        const categoryMap = {};


        apiRooms.forEach(
          (room) => {

            const categoryName =
              room?.name?.trim();


            if (!categoryName) {
              return;
            }


            const categoryKey =
              categoryName.toLowerCase();


            if (!categoryMap[categoryKey]) {

              categoryMap[categoryKey] = {

                id:
                  room.roomTypeId ||
                  room.id ||
                  categoryKey,

                name:
                  categoryName,

                roomCount:
                  0,

                availableRooms:
                  0,

              };

            }


            categoryMap[
              categoryKey
            ].roomCount += 1;

          }
        );


        /* =====================================================
           ADD INVENTORY AVAILABILITY
        ===================================================== */

        Object.values(
          categoryMap
        ).forEach(
          (category) => {

            const matchingRoom =
              apiRooms.find(
                (room) =>
                  (
                    room?.name ||
                    ""
                  )
                    .trim()
                    .toLowerCase() ===
                  category.name.toLowerCase()
              );


            if (!matchingRoom) {
              return;
            }


            const roomTypeId =
              matchingRoom.roomTypeId ||
              matchingRoom.id ||
              matchingRoom._id;


            const inventory =
              inventoryMap[roomTypeId];


            if (inventory) {

              category.availableRooms =
                inventory.available;

            } else {

              category.availableRooms =
                Number(
                  matchingRoom.available ??
                  0
                );

            }

          }
        );


        const categories =
          Object.values(
            categoryMap
          );


        if (mounted) {

          setRoomCategories(
            categories
          );

          setAllRooms(
            formattedRooms
          );

          setRooms(
            formattedRooms
          );

        }


      } catch (error) {

        console.error(
          "❌ LOAD ROOMS ERROR:",
          error
        );


        console.error(
          "Response:",
          error?.response?.data
        );


        if (mounted) {

          setRooms([]);

          setAllRooms([]);

          setRoomCategories([]);

        }


      } finally {

        if (mounted) {

          setLoadingRooms(false);

        }

      }

    };


    loadRooms();


    return () => {
      mounted = false;
    };

  }, [subdomain]);


  return {

    allRooms,

    roomsData,

    setRooms,

    roomCategories,

    setRoomCategories,

    loadingRooms,

    searchingAvailability,

    setSearchingAvailability,

  };
};


export default useBookingRooms;
